import { HttpErrorResponse } from '@angular/common/http';

/**
 * Single source of truth for turning any thrown error into a short, human
 * message safe to show a user. Layered on purpose (odoo-5133):
 *
 *   1. Business code — the API wraps failures in an ApiResult envelope
 *      `{ success, code, message }` (camelCased on the wire). The `code`
 *      (e.g. KEY_ALREADY_EXISTS) maps to a specific, actionable, translated
 *      sentence. This is the most useful tier.
 *   1b. Validation dictionary — a 400 from `[ApiController]` carries
 *      `{ errors: { Field: [reason] } }` with neither code nor message. Naming
 *      the fields turns "the request is invalid" into something the admin can
 *      act on, and into something a tester can report back to us.
 *   2. HTTP status — for infrastructure / auth / generic failures (network,
 *      401, 403, 5xx) where the body is absent or not user-safe.
 *   3. Safe server message — a remaining 4xx may carry a short, clean message
 *      worth surfacing verbatim; stack traces and oversized payloads are
 *      rejected so internals never leak to the UI.
 *
 * Pure function (no DI) so both the global error interceptor / notifications
 * service AND page-level error banners share the exact same wording.
 */

interface ApiErrorBody {
  code?: string;
  message?: string;
  /**
   * Forme des routes `api/v1/*` : `{ "error": "..." }`. Elles n'emploient ni
   * `code` ni `message`, si bien que TOUTE leur surface — validation de pack,
   * manifeste, construction — retombait dans le plancher generique.
   *
   * odoo-5133 — le testeur a valide un pack deja construit et lu « Une erreur
   * est survenue » (2026-08-09). L'API avait pourtant repondu « Update 95 is
   * already built; mutate its files or metadata to reset it to pending first »,
   * qui disait exactement quoi faire. Le message existait, personne ne le lisait.
   */
  error?: string;
  /**
   * Dictionnaire de validation d'ASP.NET (RFC 7807) : `{ "PackageId": ["..."] }`.
   * Il n'a ni `code` ni `message`, donc il tombait dans le plancher generique.
   */
  errors?: Record<string, string[]>;
}

/**
 * Nom lisible d'un champ refuse par le serveur. Les cles sont les proprietes du
 * DTO, la casse peut varier selon la source (liaison de formulaire ou corps
 * JSON), d'ou la comparaison en minuscules.
 *
 * odoo-8114 — l'API disait « The PackageId field is required », l'interface
 * affichait « Requete invalide. Verifiez votre saisie. » Le testeur n'avait
 * aucun moyen de savoir quel champ corriger, ni de nous le rapporter : la
 * regression a mis une matinee a etre identifiee alors que la reponse HTTP la
 * nommait depuis le debut.
 */
function fieldLabel(key: string): string {
  switch (key.toLowerCase()) {
    case 'name':
      return $localize`:@@error.field.name:Name`;
    case 'version':
      return $localize`:@@error.field.version:Version`;
    case 'priority':
      return $localize`:@@error.field.priority:Priority`;
    case 'type':
      return $localize`:@@error.field.type:Type`;
    case 'packageid':
      return $localize`:@@error.field.package:Package`;
    case 'downloadurl':
      return $localize`:@@error.field.downloadUrl:Download URL`;
    case 'title':
      return $localize`:@@error.field.title:Title`;
    case 'description':
      return $localize`:@@error.field.description:Description`;
    case 'releasenote':
      return $localize`:@@error.field.releaseNote:Release note`;
    case 'objectversion':
      return $localize`:@@error.field.objectVersion:Object version`;
    case 'paramversion':
      return $localize`:@@error.field.paramVersion:Param version`;
    case 'files':
      return $localize`:@@error.field.files:Files`;
    case 'installpaths':
      return $localize`:@@error.field.installPaths:Install paths`;
    case 'checksumvalue':
    case 'checksumalgorithm':
      return $localize`:@@error.field.checksum:Checksum`;
    default:
      return key;
  }
}

/**
 * Message nommant les champs refuses. Les cles techniques d'ASP.NET (`$`, ou
 * `$.champ` pour une erreur d'analyse JSON) ne designent aucun champ de
 * formulaire et sont ecartees ; s'il ne reste rien, on rend null pour laisser
 * les etages suivants repondre.
 */
function validationFieldsMessage(errors: Record<string, string[]>): string | null {
  const labels = Object.keys(errors)
    .filter((key) => key && !key.startsWith('$'))
    .map(fieldLabel);

  if (!labels.length) {
    return null;
  }

  if (labels.length === 1) {
    const field = labels[0];
    return $localize`:@@error.validationField:The "${field}:field:" field is not filled in correctly.`;
  }

  const fields = labels.join(', ');
  return $localize`:@@error.validationFields:Check these fields: ${fields}:fields:.`;
}

/** Maps an API ApiResult.Code to a user message. Returns null for unmapped codes. */
function businessCodeMessage(code: string): string | null {
  switch (code) {
    case 'KEY_NOT_FOUND':
      return $localize`:@@error.code.keyNotFound:License not found.`;
    case 'PACKAGE_NOT_FOUND':
      return $localize`:@@error.code.packageNotFound:Package not found.`;
    case 'UPDATE_NOT_FOUND':
      return $localize`:@@error.code.updateNotFound:Update not found.`;
    case 'KEY_ALREADY_EXISTS':
      return $localize`:@@error.code.keyExists:This license already exists.`;
    case 'ALREADY_EXISTS':
      return $localize`:@@error.code.alreadyExists:This item already exists.`;
    case 'KEY_ALREADY_ASSOCIATED':
      return $localize`:@@error.code.keyAssociated:This license is already linked to this package.`;
    case 'PACKAGE_OR_KEY_NOT_ASSOCIATED':
      return $localize`:@@error.code.notAssociated:This package and license are not linked.`;
    case 'INVALID_KEY_VALUE':
      return $localize`:@@error.code.invalidKey:Invalid license value.`;
    case 'VALIDATION_ERROR':
      return $localize`:@@error.code.validation:Some fields are invalid. Please check your input.`;
    default:
      return null;
  }
}

/** Generic message keyed off the HTTP status — the always-available floor. */
function statusMessage(status: number): string {
  if (status === 0) {
    return $localize`:@@error.network:Network error. Check your connection.`;
  }
  if (status === 401) {
    return $localize`:@@error.unauthorized:Session expired. Please sign in again.`;
  }
  if (status === 403) {
    return $localize`:@@error.forbidden:You do not have access to this resource.`;
  }
  if (status === 404) {
    return $localize`:@@error.notFound:Resource not found.`;
  }
  if (status === 400 || status === 422) {
    return $localize`:@@error.badRequest:The request is invalid. Please check your input.`;
  }
  if (status >= 500) {
    return $localize`:@@error.server:Server error. Please retry.`;
  }
  return $localize`:@@error.generic:Something went wrong.`;
}

/** A server-provided message is only shown if it is short and clearly not a stack trace. */
function isSafeServerMessage(message: string): boolean {
  if (message.length > 200) {
    return false;
  }
  return !/(exception|stack trace|\bSystem\.|\n\s+at\s)/i.test(message);
}

export function httpErrorMessage(error: unknown): string {
  if (typeof error === 'string') {
    return error || $localize`:@@error.generic:Something went wrong.`;
  }

  if (error instanceof HttpErrorResponse) {
    const body = error.error as ApiErrorBody | string | null | undefined;

    // 1. Business code from the ApiResult envelope.
    if (body && typeof body === 'object' && typeof body.code === 'string') {
      const mapped = businessCodeMessage(body.code);
      if (mapped) {
        return mapped;
      }
    }

    // 1b. Validation dictionary — names the offending fields. Placed before the
    //     status floor: a 400 that says which field is wrong is worth far more
    //     than "the request is invalid".
    if (body && typeof body === 'object' && body.errors && typeof body.errors === 'object') {
      const named = validationFieldsMessage(body.errors);
      if (named) {
        return named;
      }
    }

    // 2. Status-code message for infra / auth / server errors (bodies there are
    //    not user-safe, so we never surface them verbatim).
    if (error.status === 0 || error.status === 401 || error.status === 403 || error.status >= 500) {
      return statusMessage(error.status);
    }

    // 3. A safe, short server message for the remaining 4xx, else the status floor.
    //    `message` et `error` sont les deux formes en usage : les controleurs
    //    historiques rendent la premiere, les routes api/v1 la seconde.
    if (body && typeof body === 'object') {
      const serverMessage = [body.message, body.error].find(
        (m): m is string => typeof m === 'string' && m.trim().length > 0 && isSafeServerMessage(m),
      );
      if (serverMessage) {
        return serverMessage;
      }
    }

    // 3b. Corps qui EST la chaine, sans enveloppe. `BadRequest(ex.Message)` d'ASP.NET
    //     serialise le texte tel quel, si bien que le corps arrive en `string` et non en
    //     objet : les deux tests ci-dessus le manquaient et l'explication tombait au
    //     plancher.
    //
    //     odoo-5133 — le testeur a tente de nommer une mise a jour « cover-systray »
    //     (2026-08-13). Le serveur repondait pourquoi c'est refuse et quoi faire, l'IHM
    //     affichait « Requete invalide, verifiez votre saisie ». Le refus etait correct,
    //     mais rien n'indiquait ce qu'il fallait corriger — et c'est le troisieme message
    //     utile qu'on perd de cette facon.
    if (typeof body === 'string' && body.trim().length > 0 && isSafeServerMessage(body)) {
      return body.trim();
    }

    return statusMessage(error.status);
  }

  if (error instanceof Error) {
    return error.message || $localize`:@@error.generic:Something went wrong.`;
  }

  return $localize`:@@error.generic:Something went wrong.`;
}
